import { Head } from 'vite-react-ssg';
import { SITE_URL, ogLocale, type Lang } from '../lib/i18n';

interface Alternate {
  hreflang: string;
  href: string;
}

interface SeoProps {
  title: string;
  description: string;
  canonical: string;
  image?: string;
  type?: 'website' | 'article';
  jsonLd?: object | object[];
  alternates?: Alternate[];
  lang?: Lang;
}

export function Seo({
  title,
  description,
  canonical,
  image = `${SITE_URL}/og-image.png`,
  type = 'website',
  jsonLd,
  alternates = [],
  lang = 'en',
}: SeoProps) {
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Head>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale[lang]} />
      {/* Rounded CJK heading font, Japanese pages only (Korean is a unicode-range @font-face in bounce.css). */}
      {lang === 'ja' && <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@800;900&display=swap" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Head>
  );
}
